// Phase 9 R2 — Inventory admin page.
// Gating order (single source of truth): entitlement → exposure → role.
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useUserRole } from '@/hooks/useUserRole';
import { useFeatureFlag, useExposureFlag } from '@/hooks/useFeatureFlags';
import {
  useInventoryItems,
  type InventoryItem,
  type StatusFilter,
  type StockFilter,
  type SortColumn,
  type SortDir,
} from '@/hooks/inventory/useInventoryItems';
import { useSetInventoryItemActive } from '@/hooks/inventory/useSetInventoryItemActive';
import NotFound from '@/pages/NotFound';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Plus, Boxes, ShieldAlert, Loader2 } from 'lucide-react';
import { InventoryFiltersBar } from '@/components/inventory/InventoryFiltersBar';
import { InventoryTable } from '@/components/inventory/InventoryTable';
import { CreateInventoryItemDialog } from '@/components/inventory/CreateInventoryItemDialog';
import { EditInventoryItemDialog } from '@/components/inventory/EditInventoryItemDialog';
import { AdjustQuantityDialog } from '@/components/inventory/AdjustQuantityDialog';
import { RestockDialog } from '@/components/inventory/RestockDialog';
import { TransactionHistoryDialog } from '@/components/inventory/TransactionHistoryDialog';

export default function InventoryPage() {
  const { activeShop } = useAuth();
  const { role, isLoading: roleLoading } = useUserRole();

  const inventoryEnabled = useFeatureFlag('inventoryEnabled');
  const uiInventory = useExposureFlag('ui_inventory');
  const uiAdjustments = useExposureFlag('ui_inventory_adjustments');
  const uiTransactions = useExposureFlag('ui_inventory_transactions');

  // ── Gate 1: entitlement
  if (!inventoryEnabled) return <NotFound />;
  // ── Gate 2: exposure
  if (!uiInventory) return <NotFound />;

  // ── Gate 3: role (after auth resolves)
  if (roleLoading) {
    return (
      <div className="container mx-auto p-6 flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }

  const allowed = role === 'OWNER' || role === 'MANAGER';
  if (!allowed) {
    return (
      <div className="container mx-auto p-6 max-w-xl">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5" /> Access restricted
            </CardTitle>
            <CardDescription>
              Inventory can only be managed by Owners and Managers.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <InventoryAdminView
      shopId={activeShop?.id}
      showAdjustments={uiAdjustments}
      showTransactions={uiTransactions}
    />
  );
}

function InventoryAdminView({
  shopId,
  showAdjustments,
  showTransactions,
}: {
  shopId: string | undefined;
  showAdjustments: boolean;
  showTransactions: boolean;
}) {
  // Filter state
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('active');
  const [stock, setStock] = useState<StockFilter>('all');
  const [category, setCategory] = useState<string | null>(null);
  const [supplier, setSupplier] = useState<string | null>(null);
  const [sortColumn, setSortColumn] = useState<SortColumn>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  // Debounce search 250ms
  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(searchInput), 250);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  const { data: items = [], isLoading } = useInventoryItems(shopId, {
    status, stock, category, supplier, search: debouncedSearch, sortColumn, sortDir,
  });

  // Distinct category/supplier dropdown options derived from current page rows
  const categories = useMemo(() => {
    const set = new Set<string>();
    for (const i of items) if (i.category) set.add(i.category);
    return Array.from(set).sort();
  }, [items]);
  const suppliers = useMemo(() => {
    const set = new Set<string>();
    for (const i of items) if (i.supplier) set.add(i.supplier);
    return Array.from(set).sort();
  }, [items]);

  // Dialog state
  const [createOpen, setCreateOpen] = useState(false);
  const [editItem, setEditItem] = useState<InventoryItem | null>(null);
  const [adjustItem, setAdjustItem] = useState<InventoryItem | null>(null);
  const [restockItem, setRestockItem] = useState<InventoryItem | null>(null);
  const [historyItem, setHistoryItem] = useState<InventoryItem | null>(null);

  const setActive = useSetInventoryItemActive();

  return (
    <div className="container mx-auto p-4 md:p-6 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <Boxes className="h-5 w-5" /> Inventory
          </h1>
          <p className="text-sm text-muted-foreground">
            Track stocked parts, on-hand quantities, and reorder thresholds.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4 mr-2" /> Add Item
        </Button>
      </div>

      <Card>
        <CardHeader>
          <InventoryFiltersBar
            search={searchInput}
            onSearchChange={setSearchInput}
            status={status}
            onStatusChange={setStatus}
            stock={stock}
            onStockChange={setStock}
            category={category}
            onCategoryChange={setCategory}
            supplier={supplier}
            onSupplierChange={setSupplier}
            sortColumn={sortColumn}
            onSortColumnChange={setSortColumn}
            sortDir={sortDir}
            onSortDirChange={setSortDir}
            categories={categories}
            suppliers={suppliers}
          />
        </CardHeader>
        <CardContent className="p-0">
          <InventoryTable
            items={items}
            isLoading={isLoading}
            showAdjustments={showAdjustments}
            showTransactions={showTransactions}
            onEdit={setEditItem}
            onAdjust={setAdjustItem}
            onRestock={setRestockItem}
            onHistory={setHistoryItem}
            onToggleActive={(item) =>
              setActive.mutate({ id: item.id, active: !item.is_active })
            }
          />
        </CardContent>
      </Card>

      <CreateInventoryItemDialog open={createOpen} onOpenChange={setCreateOpen} />
      <EditInventoryItemDialog
        item={editItem}
        open={!!editItem}
        onOpenChange={(v) => !v && setEditItem(null)}
      />
      {showAdjustments && (
        <>
          <AdjustQuantityDialog
            item={adjustItem}
            open={!!adjustItem}
            onOpenChange={(v) => !v && setAdjustItem(null)}
          />
          <RestockDialog
            item={restockItem}
            open={!!restockItem}
            onOpenChange={(v) => !v && setRestockItem(null)}
          />
        </>
      )}
      {showTransactions && (
        <TransactionHistoryDialog
          item={historyItem}
          open={!!historyItem}
          onOpenChange={(v) => !v && setHistoryItem(null)}
        />
      )}
    </div>
  );
}
